import { h } from 'vue';
import { createColumnHelper } from '@tanstack/vue-table';
import { Checkbox } from '@n11/so-ui-components';
import BasicInput from '@/components/DataTable/BasicInput';
import VariantCell from '@/components/DataTable/VariantCell';
import ImageCell from '@/components/DataTable/ImageCell';
import BasicNativeSelect from '@/components/DataTable/BasicNativeSelect';
import DeleteButton from '../partials/DeleteButton';
import TableHeadTooltip from '@/components/DataTable/TableHeadTooltip';
import store from '@/store';
import {
  SINGULAR_PRODUCT_TABLE_COLUMNS,
  SINGULAR_PRODUCT_TABLE_COLUMN_NAMES,
  VAT_RATES,
} from '@/utils/enum/tableColumns';

const minStock = 0;
const maxStock = 99999;

export default function useColumnService(category, selectedCurrency) {
  const columnHelper = createColumnHelper();
  const columns = [];

  const formatPrice = (price) =>
    parseFloat(price).toLocaleString('tr-TR', {
      style: 'currency',
      currency: 'TRY',
    });

  const asyncValidationMethods = {
    salePrice: (value) => {
      const numericValue = parseFloat(value);
      const action = 'validations/getPriceConfirmation';
      const params = {
        categoryId: category,
        price:
          numericValue * (selectedCurrency.rate ? selectedCurrency.rate : 1),
      };
      return store.dispatch(action, params);
    },
    listPrice: (value) => {
      const numericValue = parseFloat(value);
      const action = 'validations/getPriceConfirmation';
      const params = {
        categoryId: category,
        price:
          numericValue * (selectedCurrency.rate ? selectedCurrency.rate : 1),
      };
      return store.dispatch(action, params);
    },
    duration: (value) => {
      const action = 'validations/confirmPreparationTime';
      const params = { preparationTime: value, categoryId: category };
      return store.dispatch(action, params);
    },
    stockCode: (value) => {
      const action = 'validations/confirmStockCode';
      const params = { stockCode: value };
      return store.dispatch(action, params);
    },
  };

  const validationMethods = {
    listPrice: (value, row, field) => {
      const numericValue = parseFloat(value);
      const salePrice = row.salePrice;
      if (!numericValue && row.validations[field].isDirty) {
        row.validations[field].error = true;
        row.validations[field].message = 'Satış fiyatı girin';
        return false;
      }

      if (numericValue === 0) {
        row.validations[field].error = true;
        row.validations[field].message = 'Piyasa Satış Fiyatı 0 olamaz.';
        return false;
      }

      if (numericValue < parseFloat(salePrice)) {
        row.validations[field].error = true;
        row.validations[field].message =
          "Piyasa Satış Fiyatı, n11 Satış Fiyatı'ndan düşük olamaz.";
        return false;
      }

      row.validations[field].error = false;
      row.validations[field].message = null;
      return true;
    },
    salePrice: (value, row, field) => {
      const val = parseFloat(value);
      const listPrice = row.listPrice;

      if (!val && row.validations[field].isDirty) {
        row.validations[field].error = true;
        row.validations[field].message = 'Satış fiyatı girin';
        return false;
      }

      if (val === 0) {
        row.validations[field].error = true;
        row.validations[field].message = 'n11 Satış Fiyatı 0 olamaz.';
        return false;
      }

      if (val > parseFloat(listPrice)) {
        row.validations[field].error = true;
        row.validations[field].message =
          'n11 Satış Fiyatı Piyasa Satış Fiyatından büyük olamaz.';
        return false;
      }

      row.validations[field].error = false;
      row.validations[field].message = null;
      return true;
    },
    duration: (value, row, field) => {
      const val = Number(value);

      if (!val && row.validations[field].isDirty) {
        row.validations[field].error = true;
        row.validations[field].message = 'Kargolama Süresi girin';
        return false;
      }

      if (isNaN(value)) {
        row.validations[field].error = true;
        row.validations[field].message = 'Hazırlık süresi numerik olmalıdır.';
        return false;
      }

      if (val < 0) {
        row.validations[field].error = true;
        row.validations[field].message =
          'Hazırlık süresi sıfırdan büyük olmalıdır.';
        return false;
      }

      row.validations[field].error = false;
      row.validations[field].message = null;
      return true;
    },
    stockCode: (value, row, field, cells) => {
      if (!value && row.validations[field].isDirty) {
        row.validations[field].error = true;
        row.validations[field].message = 'Stok kodu girin';
        return false;
      }

      if (cells && cells.length) {
        const excludeNulls = cells.filter((c) => c !== null && c !== '');
        if (cells && value && excludeNulls.includes(value)) {
          row.validations[field].error = true;
          row.validations[field].message =
            'Bu stok kodunu kullandığınız için değiştirmelisiniz.';
          return false;
        } else {
          row.validations[field].error = false;
          row.validations[field].message = null;
          return true;
        }
      }

      row.validations[field].error = false;
      row.validations[field].message = null;
      return true;
    },
    gtin: (value, row, field, cells) => {
      // const val = Number(value);

      if (isNaN(value)) {
        row.validations[field].error = true;
        row.validations[field].message = 'Barkod numerik olmalıdır.';
        return false;
      }

      if (cells && cells.length) {
        const excludeNulls = cells.filter((c) => c !== null && c !== '');
        if (cells && value && excludeNulls.includes(value)) {
          row.validations[field].error = true;
          row.validations[field].message =
            'Bu barkodu kullandığınız için değiştirmelisiniz.';
          return false;
        } else {
          row.validations[field].error = false;
          row.validations[field].message = null;
          return true;
        }
      }

      row.validations[field].error = false;
      row.validations[field].message = null;
      return true;
    },

    stock: (value, row, field) => {
      const val = Number(value);
      if (!val && row.validations[field].isDirty && value !== '0') {
        row.validations[field].error = true;
        row.validations[field].message = 'Stok girin';
        return false;
      }

      if (val < minStock) {
        row.validations[field].error = true;
        row.validations[field].message =
          'Stok bilgisi sıfır veya sıfırdan büyük olmalıdır.';
        return false;
      }

      if (val > maxStock) {
        row.validations[field].error = true;
        row.validations[field].message =
          `Stok bilgisi max ${maxStock} olmalıdır.`;
        return false;
      }

      row.validations[field].error = false;
      row.validations[field].message = null;
      return true;
    },

    vatRate: (value, row, field) => {
      if (
        row.validations[field].isDirty &&
        (value === undefined || value === '' || value === null)
      ) {
        row.validations[field].error = true;
        row.validations[field].message = 'KDV Oranı seçin';
        return false;
      }

      if (
        row.validations[field].isDirty &&
        !Object.keys(VAT_RATES).includes(value)
      ) {
        row.validations[field].error = true;
        row.validations[field].message =
          `KDV değeri %${Object.keys(VAT_RATES).join(', %')} olarak girilmelidir.`;
        return false;
      }

      row.validations[field].error = false;
      row.validations[field].message = null;
      return true;
    },
  };

  if (
    SINGULAR_PRODUCT_TABLE_COLUMN_NAMES.includes(
      SINGULAR_PRODUCT_TABLE_COLUMNS.SELECT_COLUMN,
    )
  ) {
    columns.push(
      columnHelper.accessor('select-col', {
        header: generateCheckboxHeader,
        cell: generateCheckboxCell,
        size: 45,
        noVariantsSize: 0,
      }),
    );
  }

  if (
    SINGULAR_PRODUCT_TABLE_COLUMN_NAMES.includes(
      SINGULAR_PRODUCT_TABLE_COLUMNS.IMAGES,
    )
  ) {
    columns.push(
      columnHelper.accessor('images', {
        header: () => 'Görsel',
        cell: ({ row, getValue }) => {
          return h(ImageCell, {
            pid: row.original.id,
            data: getValue(),
          });
        },
      }),
    );
  }

  if (
    SINGULAR_PRODUCT_TABLE_COLUMN_NAMES.includes(
      SINGULAR_PRODUCT_TABLE_COLUMNS.VARIANT_INFO,
    )
  ) {
    columns.push(
      columnHelper.accessor('name', {
        header: () => 'Varyant Bilgisi',
        cell: ({ row, getValue }) => {
          const imgUrl = row.original.imgUrl;
          return h(VariantCell, {
            content: getValue() || '',
            imgUrl: imgUrl,
            tooltipTrigger: 'hover',
            tooltipPosition: 'right',
            tooltipWidth: 378,
            showArrow: true,
            offsetTooltipImage: [0, 12],
          });
        },
        size: 106,
        noVariantsSize: 0,
      }),
    );
  }

  if (
    SINGULAR_PRODUCT_TABLE_COLUMN_NAMES.includes(
      SINGULAR_PRODUCT_TABLE_COLUMNS.GTIN,
    )
  ) {
    columns.push(
      columnHelper.accessor('gtin', {
        header: () => 'Barkod',
        cell: ({ cell, row, table }) => {
          const { accessorKey } = cell.column.columnDef;
          const rows = table.options.meta?.getColumnDataExceptThis(
            accessorKey,
            row.id,
          );
          const original = row.original;
          return h(BasicInput, {
            value: original[accessorKey],
            isValid: validate(
              original[accessorKey],
              accessorKey,
              original,
              false,
              rows,
            ),
            type: 'text',
            isOnlyNumber: true,
            maxLength: 14,
            blur: (value) => blur(value, accessorKey, original, table),
            inputId: `${accessorKey}-${original.id}`,
            errorTooltipText: getErrorTooltipText(
              original.validations,
              accessorKey,
            ),
          });
        },
        size: 141,
        noVariantsSize: 0,
      }),
    );
  }

  const validate = (value, field, row, force, rows) => {
    if (
      [
        'id',
        'images',
        'imagesRowSpan',
        'attributes',
        'hashPlain',
        'order',
        'isSingle',
        'hash',
        'validations',
      ].includes(field)
    )
      return true;
    if (force) {
      row.validations[field].isDirty = true;
      forceValidate(value, field, row, rows);
    }
    return row.validations[field].isDirty ? row.validations[field].valid : true;
  };

  async function forceValidate(value, field, row, rows) {
    row.validations[field].loading = true;
    row.validations[field].isDirty = true;

    const valid = validationMethods[field](value, row, field, rows);
    row.validations[field].valid = valid;

    if (
      valid &&
      Object.prototype.hasOwnProperty.call(asyncValidationMethods, field)
    ) {
      try {
        row.validations[field].error = false;
        row.validations[field].message = null;
        row.validations[field].valid = true;
        const response = await asyncValidationMethods[field](
          value,
          row,
          field,
          rows,
        );
        if (field === 'stockCode' && response?.data?.exists) {
          const error = new Error('Stok kodu bulunamadı');
          error.data = { message: response?.data?.message };
          throw error;
        }
      } catch (e) {
        row.validations[field].error = true;
        row.validations[field].message = e?.data?.message;
        row.validations[field].valid = false;
      }
    }

    row.validations[field].loading = false;
  }

  async function blur(value, field, row, table, rows) {
    row.validations[field].loading = true;
    row.validations[field].isDirty = true;

    const valid = validationMethods[field](value, row, field, rows);
    row.validations[field].valid = valid;

    if (
      valid &&
      Object.prototype.hasOwnProperty.call(asyncValidationMethods, field)
    ) {
      try {
        row.validations[field].error = false;
        row.validations[field].message = null;
        row.validations[field].valid = true;
        const response = await asyncValidationMethods[field](
          value,
          row,
          field,
          rows,
        );
        if (field === 'stockCode' && response?.data?.exists) {
          const error = new Error('Stok kodu bulunamadı');
          error.data = { message: response?.data?.message };
          throw error;
        }
      } catch (e) {
        row.validations[field].error = true;
        row.validations[field].message = e?.data?.message;
        row.validations[field].valid = false;
      }
    }

    row.validations[field].loading = false;
    table.options.meta?.updateRow(row.id, field, value);
  }

  const getErrorTooltipText = (validations, accessor) =>
    validations[accessor].error && validations[accessor].message
      ? validations[accessor].message
      : null;
  const getIsLoading = (validations, accessor) => {
    return validations[accessor].loading;
  };

  if (
    SINGULAR_PRODUCT_TABLE_COLUMN_NAMES.includes(
      SINGULAR_PRODUCT_TABLE_COLUMNS.STOCK_CODE_WITH_INPUT,
    )
  ) {
    columns.push(
      columnHelper.accessor('stockCode', {
        header: () => 'Stok Kodu',
        cell: ({ cell, row, table }) => {
          const { accessorKey } = cell.column.columnDef;
          const rows = table.options.meta?.getColumnDataExceptThis(
            accessorKey,
            row.id,
          );
          const original = row.original;
          return h(BasicInput, {
            value: original[accessorKey],
            isValid: validate(
              original[accessorKey],
              accessorKey,
              original,
              false,
              rows,
            ),
            isLoading: getIsLoading(original.validations, accessorKey),
            blur: (value) => blur(value, accessorKey, original, table, rows),
            inputId: `${accessorKey}-${original.id}`,
            errorTooltipText: getErrorTooltipText(
              original.validations,
              accessorKey,
            ),
          });
        },
        size: 117,
        noVariantsSize: 180,
      }),
    );
  }

  if (
    SINGULAR_PRODUCT_TABLE_COLUMN_NAMES.includes(
      SINGULAR_PRODUCT_TABLE_COLUMNS.LIST_PRICE,
    )
  ) {
    columns.push(
      columnHelper.accessor('listPrice', {
        header: () =>
          h(TableHeadTooltip, {
            title: 'Piyasa<br /> Satış Fiyatı',
            tooltipTrigger: 'click',
            tooltipText:
              'Piyasa Satış Fiyatı, ürünün indirim uygulanmadan önce son 30 gündeki minimum satış fiyatı olmalıdır. Bu alana girdiğiniz fiyat, n11’deki ürün sayfasına yansımayacaktır.',
            containerClass: 'space-between',
          }),
        cell: ({ cell, row, table }) => {
          const { accessorKey } = cell.column.columnDef;
          const original = row.original;

          let tlValue = '';
          const price = parseFloat(original[accessorKey]);
          if (
            !isNaN(price) &&
            selectedCurrency &&
            selectedCurrency.rate &&
            selectedCurrency.currencyCode !== 'TRY'
          ) {
            const tl = price * selectedCurrency.rate;
            tlValue = `₺${tl.toLocaleString('tr-TR', { minimumFractionDigits: 2, maximumFractionDigits: 3 })}`;
          }
          return h('div', {}, [
            h(BasicInput, {
              value: original[accessorKey],
              isValid: validate(original[accessorKey], accessorKey, original),
              inputId: `${accessorKey}-${original.id}`,
              type: 'currency',
              isPrice: true,
              disableAutoFocus: true,
              isLoading: getIsLoading(original.validations, accessorKey),
              blur: (value) => blur(value, accessorKey, original, table),
              format: formatPrice,
              currencyDisplay: 'symbol',
              currencyCode: selectedCurrency.currencyCode,
              errorTooltipText: getErrorTooltipText(
                original.validations,
                accessorKey,
              ),
            }),
            h(
              'div',
              {
                style:
                  'font-size: 10px; font-style: normal; font-weight: 400; line-height: 12px; color: #7382A0; margin-left: 10px; position: absolute;',
              },
              tlValue || '',
            ),
          ]);
        },
        size: 117,
        noVariantsSize: 170,
      }),
    );
  }

  if (
    SINGULAR_PRODUCT_TABLE_COLUMN_NAMES.includes(
      SINGULAR_PRODUCT_TABLE_COLUMNS.SALES_PRICE,
    )
  ) {
    columns.push(
      columnHelper.accessor('salePrice', {
        header: () =>
          h('span', {
            innerHTML: 'n11 <br /> Satış Fiyatı',
            class: 'prices',
          }),
        cell: ({ cell, row, table }) => {
          const { accessorKey } = cell.column.columnDef;
          const original = row.original;
          const valid = validate(original[accessorKey], accessorKey, original);
          const price = parseFloat(original[accessorKey]);

          let tlValue = '';
          if (
            !isNaN(price) &&
            selectedCurrency &&
            selectedCurrency.rate &&
            selectedCurrency.currencyCode !== 'TRY'
          ) {
            const tl = price * selectedCurrency.rate;
            tlValue = `₺${tl.toLocaleString('tr-TR', {
              minimumFractionDigits: 2,
              maximumFractionDigits: 3,
            })}`;
          }

          return h('div', {}, [
            h(BasicInput, {
              value: original[accessorKey],
              inputId: `${accessorKey}-${original.id}`,
              isValid: valid,
              isPrice: true,
              disableAutoFocus: true,
              type: 'currency',
              isLoading: getIsLoading(original.validations, accessorKey),
              blur: (value) => blur(value, accessorKey, original, table),
              format: formatPrice,
              currencyDisplay: 'symbol',
              currencyCode: selectedCurrency.currencyCode,
              errorTooltipText: getErrorTooltipText(
                original.validations,
                accessorKey,
              ),
            }),
            h(
              'div',
              {
                style:
                  'font-size: 10px; font-style: normal; font-weight: 400; line-height: 12px; color: #7382A0; margin-left: 10px; position: absolute;',
              },
              tlValue || '',
            ),
          ]);
        },
        size: 117,
        noVariantsSize: 170,
      }),
    );
  }

  if (
    SINGULAR_PRODUCT_TABLE_COLUMN_NAMES.includes(
      SINGULAR_PRODUCT_TABLE_COLUMNS.STOCK,
    )
  ) {
    columns.push(
      columnHelper.accessor('stock', {
        header: () => 'Stok (Adet)',
        cell: ({ cell, row, table }) => {
          const { accessorKey } = cell.column.columnDef;
          const original = row.original;

          return h(BasicInput, {
            value: original[accessorKey],
            isValid: validate(original[accessorKey], accessorKey, original),
            min: minStock,
            max: maxStock,
            type: 'text',
            isOnlyNumber: true,
            maxLength: 3,
            isLoading: getIsLoading(original.validations, accessorKey),
            blur: (value) => blur(value, accessorKey, original, table),
            inputId: `${accessorKey}-${original.id}`,
            errorTooltipText: getErrorTooltipText(
              original.validations,
              accessorKey,
            ),
          });
        },
        size: 87,
        noVariantsSize: 176,
      }),
    );
  }

  if (
    SINGULAR_PRODUCT_TABLE_COLUMN_NAMES.includes(
      SINGULAR_PRODUCT_TABLE_COLUMNS.DURATION,
    )
  ) {
    columns.push(
      columnHelper.accessor('duration', {
        header: () =>
          h(TableHeadTooltip, {
            title: 'Hazırlık Süresi',
            tooltipTrigger: 'click',
            tooltipText:
              'Siparişi kargo firmasına teslim edeceğiniz iş günü sayısıdır.',
            containerClass: 'space-between',
          }),
        cell: ({ cell, row, table }) => {
          const { accessorKey } = cell.column.columnDef;
          const original = row.original;

          return h(BasicInput, {
            value: original[accessorKey],
            type: 'number',
            isValid: validate(original[accessorKey], accessorKey, original),
            min: 1,
            max: 7,
            isLoading: getIsLoading(original.validations, accessorKey),
            blur: (value) => blur(value, accessorKey, original, table),
            inputId: `${accessorKey}-${original.id}`,
            errorTooltipText: getErrorTooltipText(
              original.validations,
              accessorKey,
            ),
          });
        },
        size: 140,
        noVariantsSize: 176,
      }),
    );
  }

  if (
    SINGULAR_PRODUCT_TABLE_COLUMN_NAMES.includes(
      SINGULAR_PRODUCT_TABLE_COLUMNS.VAT_RATES,
    )
  ) {
    columns.push(
      columnHelper.accessor('vatRate', {
        header: 'KDV Oranı',
        cell: ({ cell, row, table }) => {
          const { accessorKey } = cell.column.columnDef;
          const original = row.original;

          return h(BasicNativeSelect, {
            onChange: (e) => blur(e.target.value, accessorKey, original, table),
            isValid: validate(
              original[accessorKey],
              accessorKey,
              original,
              false,
            ),
            value: original[accessorKey],
            selectValue: row.original.vatRate,
            pid: `id-${row.original.id}`,
            options: VAT_RATES,
            label: 'Seç',
            errorTooltipText: getErrorTooltipText(
              original.validations,
              accessorKey,
            ),
          });
        },
        size: 100,
        noVariantsSize: 0,
      }),
    );
  }

  if (
    SINGULAR_PRODUCT_TABLE_COLUMN_NAMES.includes(
      SINGULAR_PRODUCT_TABLE_COLUMNS.ACTIONS,
    )
  ) {
    columns.push(
      columnHelper.accessor('actions', {
        id: 'actions',
        header: 'İşlemler',
        cell: ({ row }) =>
          h(DeleteButton, {
            pid: row.original.id,
          }),
        size: 72,
        noVariantsSize: 0,
      }),
    );
  }

  function generateCheckboxHeader(props) {
    const { table } = props;

    return h(
      Checkbox,
      {
        id: 'products-table-selectAll',
        checked: table.getIsAllRowsSelected(),
        indeterminate: table.getIsSomeRowsSelected(),
        onChange: table.getToggleAllRowsSelectedHandler(),
      },
      { default: () => [] },
    );
  }

  function generateCheckboxCell(props) {
    const { row } = props;

    return h(
      Checkbox,
      {
        id: `product-${row.original.id}`,
        value: row.original.id,
        productId: `product-${row.idproductId}`,
        indeterminate: row.getIsSomeSelected(),
        checked: row.getIsSelected(),
        checkBoxDisabled: !row.getCanSelect(),
        onChange: row.getToggleSelectedHandler(),
      },
      { default: () => [] },
    );
  }

  return {
    columns,
    validate,
  };
}
